"use client"

import { Link } from "react-router-dom";
import { encryptId } from "../../utils/crypto";
import { useAuth } from "../../AuthContext";
import { motion } from "framer-motion";

const decodeImage = (base64String) => {
  return `data:image/jpeg;base64,${base64String}`;
};

export default function ProfessionalCard({ professional, isRecommended, screenshot, professionalProjectTypes }) {
  const { user } = useAuth(); 
  const projectSkills = professional.project_skills ? professional.project_skills.split(',') : [];
  const professionalSkills = professional.professional_skills ? professional.professional_skills.split(',') : [];
  const projectType = professional.project_type;

  const calculateCompatibility = (professionalSkills, projectSkills, projectType, professionalProjectTypes) => {
    if (!professionalSkills || !projectSkills) return 0;
    const professionalSkillsArray = professionalSkills.map(skill => skill.toLowerCase());
    const projectSkillsArray = projectSkills.map(skill => skill.toLowerCase());
    const matchingSkills = projectSkillsArray.filter(skill => professionalSkillsArray.includes(skill));
    let matchPercentage = (matchingSkills.length / projectSkillsArray.length) * 100;
    if (professionalProjectTypes.includes(projectType)) {
      matchPercentage += 10;
    } else {
      matchPercentage -= 10;
    }
    if (matchPercentage > 100) {
      matchPercentage = 100;
    }
    return matchPercentage;
  };

  const handleAccept = async () => {
    try {
      const response = await fetch("http://localhost:5000/map-project", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientProjectId: professional.client_project_id,
          professionalId: professional.professional_id,
          status: "accepted",
        }),
      });

      const data = await response.json();
      if (response.ok) {
        await fetch("http://localhost:5000/notifications", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: professional.user_id,
            client_project_id: professional.client_project_id,
            type: "link",
            message: "You have been accepted for the project",
          }),
        });
        alert("Professional accepted successfully!");
      } else {
        alert(data.error || "Failed to accept the professional.");
      }
    } catch (error) {
      console.error("Error accepting professional:", error);
      alert("An error occurred while accepting the professional.");
    }
  };

  const compatibilityScore = calculateCompatibility(professionalSkills, projectSkills, projectType, professionalProjectTypes);
  const matchingSkills = projectSkills.filter(skill => professionalSkills.includes(skill.toLowerCase()));
  
  const scoreColor = compatibilityScore >= 80 
    ? { main: '#10b981', glow: 'rgba(16, 185, 129, 0.3)', bg: 'rgba(16, 185, 129, 0.08)' }
    : compatibilityScore >= 50 
    ? { main: '#f59e0b', glow: 'rgba(245, 158, 11, 0.3)', bg: 'rgba(245, 158, 11, 0.08)' }
    : { main: '#f87171', glow: 'rgba(248, 113, 113, 0.3)', bg: 'rgba(248, 113, 113, 0.08)' };

  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = (circumference * (100 - compatibilityScore)) / 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-glass)',
        borderRadius: '24px',
        overflow: 'hidden',
        transition: 'all 0.35s ease',
        position: 'relative',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.borderColor = `${scoreColor.main}33`;
        e.currentTarget.style.boxShadow = `0 16px 48px ${scoreColor.glow}`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = 'var(--border-glass)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Top accent gradient */}
      <div style={{
        height: '3px',
        background: `linear-gradient(90deg, transparent, ${scoreColor.main}, transparent)`,
        opacity: 0.6,
      }} />

      {isRecommended && (
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          padding: '4px 12px',
          borderRadius: '9999px',
          background: 'rgba(16, 185, 129, 0.12)',
          border: '1px solid rgba(16, 185, 129, 0.25)',
          fontSize: '0.7rem',
          fontWeight: 700,
          color: '#34d399',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          Recommended
        </div>
      )}

      <div style={{ padding: '28px 24px 24px' }}>
        {/* Avatar & Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            border: `2px solid ${scoreColor.main}33`,
            padding: '3px',
            flexShrink: 0,
          }}>
            <img
              src={decodeImage(professional.personal_image)}
              alt={professional.name}
              style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.05rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '2px',
            }}>{professional.name}</h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{professional.field_of_study}</p>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', opacity: 0.7 }}>{professional.institution_name}</p>
          </div>
        </div>

        {/* Compatibility Circle & Review */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <div style={{ position: 'relative', width: '64px', height: '64px', flexShrink: 0 }}>
            <svg width="64" height="64" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="50" cy="50" r="40" fill="transparent" stroke="var(--border-subtle)" strokeWidth="8" />
              <circle
                cx="50" cy="50" r="40"
                fill="transparent"
                stroke={scoreColor.main}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                style={{
                  transition: 'stroke-dashoffset 1s ease',
                  filter: `drop-shadow(0 0 4px ${scoreColor.glow})`,
                }}
              />
            </svg>
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: scoreColor.main,
                fontFamily: 'var(--font-display)',
              }}>
                {compatibilityScore.toFixed(0)}%
              </span>
            </div>
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {professional.scoreReview}
          </p>
        </div>

        {/* Mapped Skills */}
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '10px',
          }}>Mapped Skills</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {matchingSkills.length > 0 ? (
              matchingSkills.map((skill, i) => (
                <span key={i} style={{
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  background: scoreColor.bg,
                  border: `1px solid ${scoreColor.main}25`,
                  fontSize: '0.75rem',
                  color: scoreColor.main,
                  fontWeight: 500,
                }}>
                  {skill}
                </span>
              ))
            ) : (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                No matching skills found
              </span>
            )}
          </div>
        </div>

        {/* Experience */}
        <div style={{ marginBottom: '8px' }}>
          <h4 style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '6px',
          }}>Relevant Experience</h4>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{professional.job_title}</p>
        </div>
      </div>

      {/* Action footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 24px',
        borderTop: '1px solid var(--border-subtle)',
        background: 'var(--bg-glass)',
      }}>
        <Link
          to={`/page/${encodeURIComponent(encryptId(professional.user_id))}`}
          state={{ profile: professional.profile }}
          style={{
            padding: '10px 24px',
            borderRadius: '12px',
            border: '1px solid var(--border-glass)',
            background: 'transparent',
            color: 'var(--accent-cyan)',
            fontSize: '0.85rem',
            fontWeight: 600,
            textDecoration: 'none',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(var(--accent-primary-rgb), 0.08)';
            e.currentTarget.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.3)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'var(--border-glass)';
          }}
        >
          View Profile
        </Link>
        {user?.role === "client" && (
          <button
            onClick={handleAccept}
            style={{
              padding: '10px 24px',
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, var(--accent-cyan), var(--accent-violet))',
              color: '#fff',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.25s ease',
              boxShadow: '0 4px 16px rgba(var(--accent-primary-rgb), 0.2)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 6px 24px rgba(var(--accent-primary-rgb), 0.35)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(var(--accent-primary-rgb), 0.2)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Accept
          </button>
        )}
      </div>
    </motion.div>
  );
}

